import React, { useState, useRef, useMemo } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { ReviewCaveat, GeneratedMark, GeneratedLegend } from '../components/ReviewAid';
import LPRMapReplay from '../components/LPRMapReplay';
import CameraMapModal from '../components/CameraMapModal';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';

// ─── CSV Utilities ─────────────────────────────────────────────────────────────

function splitCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current); current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
}

// A recognized column name from each supported export, used to locate the real
// header row when a file carries junk/title rows above it (e.g. the Vehicle
// Detection Report's preamble) and to skip blank comma-only lines.
const HEADER_MARKERS = [
  'best_plate', 'epoch_time_start',   // OpenALPR / REKOR
  'PlateNumber', 'EVENT DATE',         // NYPD
  'Record #', 'Scanned By',            // Vehicle Manager detection report
];

// Turn an array of split rows into header-keyed objects. The header is the first
// row containing a known marker; rows above it (titles, blank lines) are skipped.
// Falls back to the first row so any plain single-header file still parses.
function arraysToObjects(rows) {
  if (!rows.length) return [];
  let h = rows.findIndex(cells => cells.some(c => HEADER_MARKERS.includes((c || '').trim())));
  if (h < 0) h = 0;
  const headers = rows[h].map(c => (c || '').trim());
  return rows.slice(h + 1).map(cells => {
    const row = {};
    headers.forEach((header, i) => { row[header] = (cells[i] || '').trim(); });
    return row;
  });
}

function parseCSV(text) {
  text = text.replace(/^\uFEFF/, '');
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (!lines.length) return [];
  return arraysToObjects(lines.map(splitCSVLine));
}

function readFileText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file, 'UTF-8');
  });
}

// Coerce an ExcelJS cell value into a plain string (mirrors the CDR parser).
function cellToString(v) {
  if (v == null) return '';
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object') return String(v.text || v.result || v.hyperlink || '');
  return String(v);
}

// Read a .csv or .xlsx/.xls file into an array of { header: value } row objects,
// so both file types feed the same downstream parsing.
async function readRows(file) {
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const { default: ExcelJS } = await import('exceljs');
    const buf = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buf);
    const ws = wb.worksheets[0];
    if (!ws) return [];
    const rows = [];
    ws.eachRow(row => { rows.push(row.values.slice(1).map(cellToString)); });
    return arraysToObjects(rows);
  }
  const text = await readFileText(file);
  return parseCSV(text);
}

// Canonical local timestamp (YYYY-MM-DDTHH:MM:SS) so reads from different export
// formats sort and group by date consistently regardless of original string shape.
function localIso(d) {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function parseRegion(region) {
  if (!region) return '';
  const parts = region.toUpperCase().split('-');
  return parts.length >= 2 ? parts[parts.length - 1] : parts[0];
}

function directionLabel(degreesStr, camera = '') {
  const deg = parseFloat(degreesStr);
  if (!isNaN(deg) && deg >= 0) {
    const cardinals = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return cardinals[Math.round(deg / 45) % 8];
  }
  const name = camera.toLowerCase();
  const tags = [
    [' nb', 'NB'], [' sb', 'SB'], [' eb', 'EB'], [' wb', 'WB'],
    ['northbound', 'NB'], ['southbound', 'SB'], ['eastbound', 'EB'], ['westbound', 'WB'],
  ];
  for (const [tag, label] of tags) {
    if (name.includes(tag)) return label;
  }
  return '';
}

function parseTS(tsStr) {
  if (!tsStr) return null;
  try {
    const d = new Date(tsStr);
    if (!isNaN(d)) return d;
    const d2 = new Date(tsStr.slice(0, 19));
    if (!isNaN(d2)) return d2;
    return null;
  } catch { return null; }
}

// Apply the shared GPS sanity check and attach a canonical sort key, so reads
// from any export format compare and group consistently. A read carrying no GPS
// fix (empty, 0,0, or the -1,-1 sentinel used by mobile/trailer units) keeps its
// place in the timeline but has null coords so the map can skip it.
function makeRead(r) {
  const lat = r.lat, lng = r.lng;
  const hasGps = !isNaN(lat) && !isNaN(lng) &&
    Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === -1 && lng === -1) &&
    Math.abs(lat) > 0.001;
  return {
    plate: r.plate,
    state: r.region ? parseRegion(r.region) : '',
    site: r.site,
    camera: r.camera,
    tsStr: r.tsStr,
    dt: r.dt,
    sortKey: r.dt ? localIso(r.dt) : (r.tsStr || ''),
    direction: r.direction,
    confidence: r.confidence || 0,
    agency: r.agency || '',
    lat: hasGps ? lat : null,
    lng: hasGps ? lng : null,
  };
}

// Normalize one row from a supported export into the shared read shape, or null
// if it isn't a usable plate read. Supports two layouts:
//   • OpenALPR / REKOR group-results (best_plate, epoch_time_start, camera, …)
//   • FLO / NYPD fixed+mobile LPR     (PlateNumber, EVENT DATE, Asset Name, …)
function normalizeRead(row) {
  // OpenALPR / REKOR group-results
  if (row['best_plate'] !== undefined || row['epoch_time_start'] !== undefined) {
    const plate = (row['best_plate'] || '').trim().toUpperCase();
    if (!plate) return null;
    const camera = (row['camera'] || '').trim();
    const tsStr = (row['epoch_time_start'] || '').trim();
    return makeRead({
      plate,
      region: (row['region'] || '').trim(),
      site: camera || (row['site'] || '').trim(),
      camera,
      tsStr,
      dt: parseTS(tsStr),
      direction: directionLabel((row['direction_of_travel_degrees'] || '-1').trim(), camera),
      confidence: parseFloat(row['best_confidence'] || '0') || 0,
      lat: parseFloat(row['gps_latitude']),
      lng: parseFloat(row['gps_longitude']),
    });
  }
  // FLO / NYPD LPR export
  if (row['PlateNumber'] !== undefined) {
    const plate = (row['PlateNumber'] || '').trim().toUpperCase();
    if (!plate) return null;
    const site = (row['Asset Name'] || '').trim();
    const tsStr = (row['EVENT DATE'] || '').trim();
    return makeRead({
      plate,
      region: (row['PlateState'] || '').trim(),
      site,
      camera: site,
      tsStr,
      dt: parseTS(tsStr),
      // No travel-degree field; fall back to any direction word in the site name.
      direction: directionLabel('-1', site),
      confidence: 0,
      lat: parseFloat(row['LATITUDE']),
      lng: parseFloat(row['LONGITUDE']),
    });
  }
  // Vehicle Manager detection report (no GPS; camera = "Scanned By")
  if (row['Scanned By'] !== undefined || row['Record #'] !== undefined) {
    const plate = (row['Plate'] || '').trim().toUpperCase();
    if (!plate) return null;
    const site = (row['Scanned By'] || '').trim();
    // Date + Time come split, and Time carries a trailing tz abbrev (e.g. "EDT")
    // that Date can't parse — drop it and read the wall-clock time as local.
    const dateStr = (row['Date'] || '').trim();
    const timeStr = (row['Time'] || '').trim().replace(/\s+[A-Za-z]{2,4}$/, '');
    const tsStr = `${dateStr} ${timeStr}`.trim();
    return makeRead({
      plate,
      region: (row['Registration State'] || '').trim(),
      site,
      camera: site,
      tsStr,
      dt: parseTS(tsStr),
      direction: directionLabel('-1', site),
      confidence: 0,
      agency: (row['Agency'] || '').trim(),
      // This export carries no coordinates, so these reads are never mappable.
      lat: NaN,
      lng: NaN,
    });
  }
  return null;
}

function fmtHour(h) {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

// Confidence in a prediction, from how much it's backed by data:
// the number of observed departures and how dominant the top choice is.
function predictionConfidence(total, topProb) {
  if (total >= 6 && topProb >= 0.6) return { level: 'High', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' };
  if (total >= 3 && topProb >= 0.4) return { level: 'Medium', color: '#d97706', bg: '#fffbeb', border: '#fde68a' };
  return { level: 'Low', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' };
}

function fmtGap(mins) {
  if (mins < 60) return `${mins} min`;
  const h = mins / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} hr`;
}

function fmtMins(mins) {
  if (mins == null) return '—';
  if (mins < 1) return '<1 min';
  if (mins < 90) return `~${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m ? `~${h}h ${m}m` : `~${h}h`;
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateFull(dateStr) {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

// ─── Analysis ─────────────────────────────────────────────────────────────────

async function analyzePattern(files) {
  const fileList = Array.isArray(files) ? files : [files];

  // Read and normalize every file into one shared read list, tagging each read
  // with its source file so reads from different LPR systems can be merged.
  const rawReads = [];
  const sources = [];
  for (const file of fileList) {
    const rows = await readRows(file);
    let count = 0;
    for (const row of rows) {
      const read = normalizeRead(row);
      if (!read) continue;
      read.source = file.name;
      rawReads.push(read);
      count++;
    }
    sources.push({ name: file.name, reads: count });
  }

  if (!rawReads.length) throw new Error('No valid plate reads found in the uploaded file(s).');

  // Collapse identical reads (same plate, timestamp, and site). This drops the
  // repeated rows some exports contain and avoids double-counting a hit that
  // appears in more than one uploaded file.
  const seen = new Set();
  const reads = [];
  for (const r of rawReads) {
    const key = `${r.plate}|${r.tsStr}|${r.site}`;
    if (seen.has(key)) continue;
    seen.add(key);
    reads.push(r);
  }

  // Pick the most common plate across all files
  const plateCounts = {};
  for (const r of reads) plateCounts[r.plate] = (plateCounts[r.plate] || 0) + 1;
  const primaryPlate = Object.entries(plateCounts).sort((a, b) => b[1] - a[1])[0][0];
  // Order by the canonical sort key so reads from different timestamp formats
  // interleave correctly.
  const plateReads = reads.filter(r => r.plate === primaryPlate)
    .sort((a, b) => (a.sortKey || '').localeCompare(b.sortKey || ''));
  const primaryState = (plateReads.find(r => r.state) || {}).state || '';

  // Group by date
  const byDate = {};
  for (const r of plateReads) {
    let dateKey;
    if (r.dt) {
      const y = r.dt.getFullYear();
      const mo = String(r.dt.getMonth() + 1).padStart(2, '0');
      const d = String(r.dt.getDate()).padStart(2, '0');
      dateKey = `${y}-${mo}-${d}`;
    } else if (r.tsStr && r.tsStr.length >= 10) {
      dateKey = r.tsStr.slice(0, 10);
    } else {
      dateKey = 'unknown';
    }
    if (!byDate[dateKey]) byDate[dateKey] = [];
    byDate[dateKey].push(r);
  }

  const timeline = {};
  for (const dateKey of Object.keys(byDate).sort()) {
    timeline[dateKey] = byDate[dateKey].map(r => ({
      time: r.dt
        ? r.dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        : r.tsStr,
      ts: r.dt ? r.dt.getTime() : null,
      site: r.site,
      camera: r.camera,
      direction: r.direction,
      confidence: Math.round(r.confidence * 10) / 10,
      agency: r.agency,
      lat: r.lat,
      lng: r.lng,
    }));
  }

  // Canonical (YYYY-MM-DDTHH:MM:SS) keys so the range is correct regardless of
  // each file's original timestamp format.
  const allKeys = plateReads.map(r => r.sortKey).filter(Boolean);
  const firstSeen = allKeys.length ? allKeys.reduce((a, b) => a < b ? a : b) : '';
  const lastSeen = allKeys.length ? allKeys.reduce((a, b) => a > b ? a : b) : '';

  return {
    plate: primaryPlate,
    state: primaryState,
    total_reads: plateReads.length,
    date_range: { first: firstSeen, last: lastSeen },
    timeline,
    sources,
  };
}

// ─── Stats from filtered timeline ────────────────────────────────────────────

function median(arr) {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

// maxGapMin: the most time (minutes) allowed between two consecutive reads for
// them to count as one continuous trip. Larger gaps start a new trip, so routes
// and predictions are never chained across stops (and never lost at midnight).
function computeStats(timeline, maxGapMin = 60) {
  const siteCounts = {}, hourCounts = {}, dowCounts = {}, dirCounts = {};
  const seqCounts = {}, seqCounts3 = {};
  // First-order transition model: trans[from][to] = { count, deltas: [minutes] }
  const trans = {};
  let totalReads = 0;

  // Per-read aggregates stay grouped by calendar day (for day-of-week).
  for (const [dateKey, reads] of Object.entries(timeline)) {
    totalReads += reads.length;
    const [y, mo, d] = dateKey.split('-').map(Number);
    const dow = isNaN(y) ? '' : new Date(y, mo - 1, d).toLocaleDateString('en-US', { weekday: 'short' });

    for (const r of reads) {
      if (r.site) siteCounts[r.site] = (siteCounts[r.site] || 0) + 1;
      const h = parseHourFromTimeStr(r.time);
      if (h >= 0) hourCounts[h] = (hourCounts[h] || 0) + 1;
      if (dow) dowCounts[dow] = (dowCounts[dow] || 0) + 1;
      if (r.direction) dirCounts[r.direction] = (dirCounts[r.direction] || 0) + 1;
    }
  }

  // Routes & transitions use one globally time-ordered list, segmented by gap —
  // not by calendar day — so an overnight hop (e.g. 11:50 PM → 12:01 AM) is kept.
  const ordered = Object.values(timeline).flat()
    .filter(r => r.ts != null)
    .sort((a, b) => a.ts - b.ts);
  const maxGapMs = maxGapMin * 60000;
  const linked = (i, j) => {
    const gap = ordered[j].ts - ordered[i].ts;
    return gap >= 0 && gap <= maxGapMs;
  };

  for (let i = 0; i < ordered.length - 1; i++) {
    if (!linked(i, i + 1)) continue;
    const a = ordered[i].site, b = ordered[i + 1].site;
    if (a && b && a !== b) {
      const seq = `${a} → ${b}`;
      seqCounts[seq] = (seqCounts[seq] || 0) + 1;

      if (!trans[a]) trans[a] = {};
      if (!trans[a][b]) trans[a][b] = { count: 0, deltas: [] };
      trans[a][b].count += 1;
      trans[a][b].deltas.push((ordered[i + 1].ts - ordered[i].ts) / 60000);
    }
  }

  for (let i = 0; i < ordered.length - 2; i++) {
    if (!linked(i, i + 1) || !linked(i + 1, i + 2)) continue;
    const a = ordered[i].site, b = ordered[i + 1].site, c = ordered[i + 2].site;
    if (a && b && c) {
      const seq3 = `${a} → ${b} → ${c}`;
      seqCounts3[seq3] = (seqCounts3[seq3] || 0) + 1;
    }
  }

  // Turn the transition model into per-origin predictions
  const predictions = Object.entries(trans).map(([from, tos]) => {
    const total = Object.values(tos).reduce((sum, t) => sum + t.count, 0);
    const nexts = Object.entries(tos)
      .map(([camera, t]) => ({
        camera,
        count: t.count,
        probability: total ? t.count / total : 0,
        medianMin: median(t.deltas),
        samples: t.deltas.length,
      }))
      .sort((a, b) => b.count - a.count);
    return { from, total, nexts };
  }).sort((a, b) => b.total - a.total);

  // Flatten transitions into a global, frequency-ranked list of corridors (A → B)
  const segments = [];
  for (const [from, tos] of Object.entries(trans)) {
    for (const [to, t] of Object.entries(tos)) {
      segments.push({ from, to, count: t.count, medianMin: median(t.deltas), samples: t.deltas.length });
    }
  }
  segments.sort((a, b) => b.count - a.count || (a.medianMin ?? Infinity) - (b.medianMin ?? Infinity));

  return {
    totalReads,
    sites: Object.entries(siteCounts).map(([n, c]) => ({ name: n, count: c })).sort((a, b) => b.count - a.count),
    hours: Object.entries(hourCounts).map(([h, c]) => ({ hour: parseInt(h), count: c })).sort((a, b) => a.hour - b.hour),
    days_of_week: Object.entries(dowCounts).map(([d, c]) => ({ day: d, count: c })).sort((a, b) => b.count - a.count),
    directions: Object.entries(dirCounts).map(([d, c]) => ({ dir: d, count: c })).sort((a, b) => b.count - a.count),
    sequences: Object.entries(seqCounts).map(([s, c]) => ({ seq: s, count: c })).sort((a, b) => b.count - a.count).slice(0, 5),
    sequences3: Object.entries(seqCounts3).map(([s, c]) => ({ seq: s, count: c })).sort((a, b) => b.count - a.count).slice(0, 5),
    predictions,
    segments,
  };
}

function parseHourFromTimeStr(timeStr) {
  const m = String(timeStr || '').match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return -1;
  let h = parseInt(m[1]);
  const ampm = m[3].toUpperCase();
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return h;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function BarItem({ label, count, max, labelWidth = '110px' }) {
  const pct = Math.max(count ? 3 : 0, Math.round((count / max) * 100));
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: '9px' }}>
      <Typography sx={{
        fontSize: '12px', color: '#374151',
        width: labelWidth,
        overflow: 'hidden', display: '-webkit-box',
        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        flexShrink: 0, lineHeight: 1.3,
      }} title={label}>{label}</Typography>
      <Box sx={{ flex: 1, bgcolor: '#f1f5f9', borderRadius: '4px', height: '13px', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', borderRadius: '4px', bgcolor: '#6d28d9', width: `${pct}%`, minWidth: '4px', transition: 'width .5s ease' }}/>
      </Box>
      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#6d28d9', minWidth: '20px', textAlign: 'right' }}>
        {count}
      </Typography>
    </Box>
  );
}

function CardTitle({ icon, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
      <Box sx={{ color: '#6d28d9', flexShrink: 0 }}>{icon}</Box>
      {children}
    </Box>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function LPRPatternAnalyzer() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [timeRange, setTimeRange] = useState([0, 23]);
  const [openDates, setOpenDates] = useState(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [showAllCameras, setShowAllCameras] = useState(false);
  const [maxGapMin, setMaxGapMin] = useState(240);
  const [predictFrom, setPredictFrom] = useState('');
  const [showPredictHelp, setShowPredictHelp] = useState(false);
  const [showRoutesHelp, setShowRoutesHelp] = useState(false);
  const [predOpen, setPredOpen] = useState(false);
  const [predSearch, setPredSearch] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [locModal, setLocModal] = useState(null);
  const fileInputRef = useRef();

  function addFiles(fileList) {
    trackToolAction('/lprpattern', 'LPR Pattern Analyzer');
    const incoming = Array.from(fileList || []);
    const accepted = [];
    for (const f of incoming) {
      const lower = f.name.toLowerCase();
      if (!lower.endsWith('.csv') && !lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
        alert(`"${f.name}" was skipped — please upload .csv, .xlsx, or .xls files.`);
        continue;
      }
      accepted.push(f);
    }
    if (!accepted.length) return;
    setFiles(prev => {
      // De-dupe by name + size so re-selecting the same file is a no-op.
      const sig = f => `${f.name}|${f.size}`;
      const have = new Set(prev.map(sig));
      return [...prev, ...accepted.filter(f => !have.has(sig(f)))];
    });
    setResults(null);
    setError('');
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setResults(null);
    setError('');
  }

  function clearFiles() {
    setFiles([]);
    setResults(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function runAnalysis() {
    if (!files.length) return;
    setLoading(true);
    setError('');
    setResults(null);
    try {
      const data = await analyzePattern(files);
      setResults(data);
      setTimeRange([0, 23]);
      setOpenDates(new Set(Object.keys(data.timeline)));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // Filter timeline by time range
  const filteredTimeline = useMemo(() => {
    if (!results) return {};
    const [minH, maxH] = timeRange;
    const out = {};
    for (const [dateKey, reads] of Object.entries(results.timeline)) {
      const filtered = reads.filter(r => {
        const h = parseHourFromTimeStr(r.time);
        return h >= minH && h <= maxH;
      });
      if (filtered.length) out[dateKey] = filtered;
    }
    return out;
  }, [results, timeRange]);

  const stats = useMemo(() => computeStats(filteredTimeline, maxGapMin), [filteredTimeline, maxGapMin]);

  // Lookup from origin camera -> prediction, plus the resolved selected origin
  const predMap = useMemo(
    () => Object.fromEntries((stats.predictions || []).map(p => [p.from, p])),
    [stats]
  );
  // Default origin = the camera of the most recent read. If that exact camera has
  // no onward moves anywhere in the data, walk back to the most recent read that does.
  const defaultFrom = useMemo(() => {
    const dates = Object.keys(filteredTimeline).filter(d => /^\d{4}-/.test(d)).sort();
    if (dates.length) {
      const latest = filteredTimeline[dates[dates.length - 1]];
      for (let i = latest.length - 1; i >= 0; i--) {
        if (latest[i].site && predMap[latest[i].site]) return latest[i].site;
      }
    }
    return stats.predictions && stats.predictions.length ? stats.predictions[0].from : '';
  }, [filteredTimeline, predMap, stats]);

  const activeFrom = (predictFrom && predMap[predictFrom]) ? predictFrom : defaultFrom;

  function toggleDate(dateKey) {
    setOpenDates(prev => {
      const next = new Set(prev);
      next.has(dateKey) ? next.delete(dateKey) : next.add(dateKey);
      return next;
    });
  }

  function downloadPDF() {
    if (!results) return;
    const plate = results.plate || 'LPR';
    const origTitle = document.title;
    document.title = `LPR_Pattern_${plate}_${new Date().toISOString().slice(0, 10)}`;
    window.print();
    document.title = origTitle;
  }

  // How many reads carry GPS — drives the "not all reads are mapped" callout
  // when some (mobile units) or all (Vehicle Manager exports) lack coordinates.
  let mappedReads = 0, totalReads = 0;
  if (results) {
    for (const reads of Object.values(results.timeline)) {
      for (const r of reads) { totalReads++; if (r.lat != null && r.lng != null) mappedReads++; }
    }
  }
  const unmappedReads = totalReads - mappedReads;

  const dr = results?.date_range || {};
  const firstStr = dr.first ? dr.first.substring(0, 10) : '';
  const lastStr = dr.last ? dr.last.substring(0, 10) : '';
  const dateRangeDisplay = firstStr === lastStr
    ? (firstStr ? `Observed on ${fmtDate(firstStr)}` : '')
    : (firstStr && lastStr ? `${fmtDate(firstStr)} — ${fmtDate(lastStr)}` : '');

  return (
    <>
      <style>{`
        @media print {
          /* Force background colors/bars/chips to render in the printout */
          *, *::before, *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Hide interactive-only chrome from the PDF */
          .lpr-no-print { display: none !important; }
        }
      `}</style>
      <Navbar pageTitle="LPR Pattern Analyzer" />
      <Container maxWidth="md" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>
            LPR Pattern Analyzer
          </Typography>
          <Typography sx={{ color: '#64748b', mt: '6px', fontSize: '15px' }}>
            Upload one or more single-vehicle LPR files to detect movement patterns across time.
          </Typography>
          <Typography sx={{ color: '#94a3b8', mt: '4px', fontSize: '13px', fontStyle: 'italic' }}>
            Works with REKOR / OpenALPR, NYPD, and Motorola Vigilant LPR exports (.csv, .xlsx, .xls).
            Upload several files for the same plate and they'll be merged into one analysis.
          </Typography>
          <ReviewCaveat what="the original LPR records from the system that produced them" style={{ marginTop: 14 }} />
          <GeneratedLegend style={{ marginTop: 8 }}>
            Marks results generated by LE Tech Tools (routes and predictions). They are not part of the original LPR records.
          </GeneratedLegend>
        </Box>

        {/* Upload card */}
        <Paper className="lpr-no-print" elevation={0} sx={{
          borderRadius: '14px',
          boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
          p: '26px 26px 22px', mb: 2.5,
        }}>
          <CardTitle icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          }>Upload LPR Data (Single Vehicle)</CardTitle>

          {/* Drop zone */}
          <Box
            onClick={() => fileInputRef.current.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files); }}
            sx={{
              border: `2px dashed ${dragOver ? '#6d28d9' : '#cbd5e1'}`,
              borderRadius: '10px', p: '36px 24px', textAlign: 'center',
              cursor: 'pointer', transition: 'all .2s',
              bgcolor: dragOver ? '#f5f3ff' : 'transparent',
              '&:hover': { borderColor: '#6d28d9', bgcolor: '#f5f3ff' },
            }}
          >
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"
              style={{ display: 'block', margin: '0 auto 10px' }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <Typography sx={{ fontSize: '14px', color: '#64748b' }}>
              <strong style={{ color: '#6d28d9' }}>Drop LPR files here</strong> or click to browse
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#64748b', mt: '4px' }}>
              One vehicle — add one or more files (OpenALPR, NYPD, or Motorola Vigilant) and
              the tool will identify the plate automatically
            </Typography>
            <input
              ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" multiple
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files.length) addFiles(e.target.files); e.target.value = ''; }}
            />
          </Box>

          {/* File list */}
          {files.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {files.map((f, i) => (
                <Box key={`${f.name}|${f.size}`} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  p: '11px 16px', bgcolor: '#f5f3ff',
                  border: '1px solid #ddd6fe', borderRadius: '8px',
                  mt: i === 0 ? 0 : 1,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6d28d9" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <path d="M14 2v6h6"/>
                  </svg>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#4c1d95', flex: 1, wordBreak: 'break-all' }}>
                    {f.name}
                  </Typography>
                  <Box
                    component="button" onClick={() => removeFile(i)}
                    sx={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#94a3b8', fontSize: '20px', lineHeight: 1, p: '2px 4px',
                      '&:hover': { color: '#dc2626' },
                    }}
                  >&times;</Box>
                </Box>
              ))}
              {files.length > 1 && (
                <Box
                  component="button" onClick={clearFiles}
                  sx={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#94a3b8', fontSize: '12px', fontWeight: 600, mt: 1, p: '2px 4px',
                    fontFamily: 'inherit', '&:hover': { color: '#dc2626' },
                  }}
                >Clear all {files.length} files</Box>
              )}
            </Box>
          )}

          {error && (
            <Box sx={{ bgcolor: '#fef2f2', color: '#dc2626', p: '10px 14px', borderRadius: '8px', mt: 1.5, fontSize: '14px' }}>
              {error}
            </Box>
          )}

          <Button
            variant="contained" fullWidth
            onClick={runAnalysis}
            disabled={!files.length || loading}
            startIcon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            }
            sx={{
              mt: 2, p: '10px 20px', bgcolor: '#6d28d9',
              fontSize: '14px', fontWeight: 600, borderRadius: '8px',
              '&:hover': { bgcolor: '#5b21b6' },
              '&:disabled': { bgcolor: '#94a3b8' },
            }}
          >
            {loading ? 'Analyzing…' : 'Analyze Patterns'}
          </Button>
        </Paper>

        {/* Results */}
        {results && (
          <>
            {/* Plate banner */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2.5,
              p: '18px 22px',
              background: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)',
              borderRadius: '12px', mb: 2.75, color: '#fff', flexWrap: 'wrap',
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '11px', opacity: .65, textTransform: 'uppercase', letterSpacing: '.08em', mb: 1 }}>
                  Vehicle Identified
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                  <Typography sx={{
                    fontSize: '28px', fontWeight: 900, letterSpacing: '3px',
                    fontFamily: "'Courier New', monospace",
                    bgcolor: '#fff', color: '#1e293b',
                    px: '16px', py: '6px', borderRadius: '6px',
                    border: '3px solid #e2e8f0',
                  }}>
                    {results.plate}
                  </Typography>
                  {results.state && (
                    <Box sx={{
                      fontSize: '12px', bgcolor: 'rgba(255,255,255,.18)',
                      px: '10px', py: '3px', borderRadius: '99px',
                      fontWeight: 700, letterSpacing: '.04em',
                    }}>
                      {results.state}
                    </Box>
                  )}
                </Box>
                {dateRangeDisplay && (
                  <Typography sx={{ fontSize: '13px', opacity: .8, mt: 1 }}>
                    {dateRangeDisplay}
                  </Typography>
                )}
                {results.sources && results.sources.length > 1 && (
                  <Typography sx={{ fontSize: '12px', opacity: .7, mt: .5 }}>
                    Merged from {results.sources.map(s => `${s.name} (${s.reads})`).join(', ')}
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: 'right', ml: 'auto' }}>
                <Typography sx={{ fontSize: '32px', fontWeight: 800, lineHeight: 1 }}>
                  {stats.totalReads}
                </Typography>
                <Typography sx={{ fontSize: '11px', opacity: .7, textTransform: 'uppercase', letterSpacing: '.07em', mt: '2px' }}>
                  Total Reads
                </Typography>
              </Box>
            </Box>

            {/* Map replay */}
            <Paper className="lpr-no-print" elevation={0} sx={{
              borderRadius: '14px',
              boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
              p: '26px 26px 22px', mb: 2.5,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <CardTitle icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                    <line x1="8" y1="2" x2="8" y2="18"/>
                    <line x1="16" y1="6" x2="16" y2="22"/>
                  </svg>
                }>Map Replay</CardTitle>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowMap(v => !v)}
                  sx={{
                    border: 'none', background: '#6d28d9', cursor: 'pointer',
                    color: '#fff', fontWeight: 700, fontSize: '12px', borderRadius: '8px',
                    px: '12px', py: '7px', fontFamily: 'inherit', flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: '6px',
                    '&:hover': { background: '#5b21b6' },
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                  {showMap ? 'Hide map' : 'Visualize on map'}
                </Box>
              </Box>
              <Typography sx={{ fontSize: '13px', color: '#64748b', mt: '2px', mb: (showMap || unmappedReads > 0) ? 2 : 0 }}>
                Plot every GPS-tagged read on a map and animate the vehicle's movement — playback is scaled
                to the real time between hits, with an option to skip long idle gaps. Drag the window slider
                to replay the whole history or just a portion.
              </Typography>
              {unmappedReads > 0 && (
                <Box sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1,
                  bgcolor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
                  borderRadius: '8px', p: '10px 14px', mb: showMap ? 2 : 0, fontSize: '13px',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    style={{ flexShrink: 0, marginTop: '1px' }}>
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <span>
                    {mappedReads === 0
                      ? "These reads don't include location data, so none of them can be shown on the map."
                      : `${unmappedReads} of ${totalReads} reads have no location data and aren't shown on the map.`}
                  </span>
                </Box>
              )}
              {showMap && <LPRMapReplay timeline={results.timeline} />}
            </Paper>

            {/* Time filter */}
            <Paper elevation={0} sx={{
              borderRadius: '14px',
              boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
              p: '26px 26px 22px', mb: 2.5,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <CardTitle icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                }>Filter by Time of Day</CardTitle>
                <Box sx={{
                  fontSize: '13px', fontWeight: 700, color: '#6d28d9',
                  bgcolor: '#f5f3ff', px: '10px', py: '3px', borderRadius: '99px',
                }}>
                  {timeRange[0] === 0 && timeRange[1] === 23
                    ? 'All hours'
                    : `${fmtHour(timeRange[0])} – ${fmtHour(timeRange[1])}`}
                </Box>
              </Box>
              <Slider
                value={timeRange}
                onChange={(e, val) => setTimeRange(val)}
                min={0} max={23} step={1}
                valueLabelDisplay="auto"
                valueLabelFormat={fmtHour}
                sx={{
                  color: '#6d28d9',
                  '& .MuiSlider-thumb': {
                    '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 8px rgba(109,40,217,.16)' },
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                {['12 AM', '6 AM', '12 PM', '6 PM', '11 PM'].map(t => (
                  <Typography key={t} sx={{ fontSize: '10px', color: '#94a3b8' }}>{t}</Typography>
                ))}
              </Box>
              <Typography sx={{ fontSize: '12px', color: '#94a3b8', mt: 1.25, textAlign: 'center' }}>
                {stats.totalReads === 0
                  ? 'No reads in this time window.'
                  : `${stats.totalReads} read${stats.totalReads !== 1 ? 's' : ''} in selected window`}
              </Typography>
            </Paper>

            {/* Stats grid — 2 columns */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2.5, mb: 2.5 }}>

              {/* Site frequency */}
              <Paper elevation={0} sx={{
                borderRadius: '14px',
                boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
                p: '26px 26px 22px',
              }}>
                <CardTitle icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                }>Camera Frequency</CardTitle>
                {stats.sites.length
                  ? (() => {
                      const maxCount = Math.max(1, ...stats.sites.map(x => x.count));
                      const visible = showAllCameras ? stats.sites : stats.sites.slice(0, 15);
                      const hidden = stats.sites.length - 15;
                      return (
                        <>
                          {visible.map(s => (
                            <BarItem key={s.name} label={s.name} count={s.count} max={maxCount} labelWidth='150px' />
                          ))}
                          {hidden > 0 && (
                            <Box
                              component="button"
                              type="button"
                              onClick={() => setShowAllCameras(v => !v)}
                              sx={{
                                mt: 1, border: 'none', background: 'none', cursor: 'pointer',
                                color: '#6d28d9', fontWeight: 700, fontSize: '13px', p: 0,
                                fontFamily: 'inherit', '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {showAllCameras ? 'Show less' : `Show ${hidden} more camera${hidden !== 1 ? 's' : ''}`}
                            </Box>
                          )}
                        </>
                      );
                    })()
                  : <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>No site data.</Typography>}
              </Paper>

              {/* Hourly activity */}
              <Paper elevation={0} sx={{
                borderRadius: '14px',
                boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
                p: '26px 26px 22px',
              }}>
                <CardTitle icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                }>Hourly Activity</CardTitle>
                {stats.hours.length
                  ? stats.hours.map(h => (
                      <BarItem key={h.hour} label={fmtHour(h.hour)} count={h.count} max={Math.max(1, ...stats.hours.map(x => x.count))} labelWidth='55px' />
                    ))
                  : <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>No time data.</Typography>}

                {stats.days_of_week.length > 0 && (
                  <Box sx={{ mt: 1.75, pt: 1.75, borderTop: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', mb: 1 }}>
                      Day of Week
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {stats.days_of_week.map(d => (
                        <Box key={d.day} sx={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center',
                          bgcolor: '#f5f3ff', borderRadius: '8px', px: '10px', py: '6px', minWidth: '44px',
                        }}>
                          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6d28d9' }}>{d.day}</Typography>
                          <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#1e293b' }}>{d.count}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>

            {/* Most-traveled corridors — full width */}
            <Paper elevation={0} sx={{
              borderRadius: '14px',
              boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
              p: '26px 26px 22px', mb: 2.5,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <CardTitle icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                  </svg>
                }>Common Routes<GeneratedMark /></CardTitle>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowRoutesHelp(v => !v)}
                  sx={{
                    border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer',
                    color: '#6d28d9', fontWeight: 700, fontSize: '12px', borderRadius: '8px',
                    px: '10px', py: '5px', fontFamily: 'inherit', flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: '5px',
                    '&:hover': { bgcolor: '#f5f3ff' },
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  {showRoutesHelp ? 'Hide' : 'How this works'}
                </Box>
              </Box>

              <Typography sx={{ fontSize: '13px', color: '#64748b', mt: '2px', mb: showRoutesHelp ? 1 : 2 }}>
                The multi-stop paths this vehicle repeated most often, with the typical travel time between
                each stop.
              </Typography>

              {showRoutesHelp && (
                <Box sx={{
                  bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
                  p: '14px 16px', mb: 2,
                }}>
                  <Typography sx={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.6 }}>
                    A <strong>route</strong> is a run of consecutive camera hits that belong to one
                    continuous trip (e.g. camera A → B → C). Routes that occurred more than once are ranked
                    by how often they repeated.
                    <br /><br />
                    • <strong>×N</strong> = how many times this exact path was traveled — a raw
                    <em> frequency</em> count, so the most-repeated routes rise to the top.<br />
                    • <strong>Travel time</strong> on each arrow = the <em>median</em> minutes between those two
                    cameras across every trip (median ignores outliers).<br />
                    • <strong>Trip gap</strong> = the slider below. Two back-to-back reads count as the same
                    trip only if they're within this many minutes of each other; a larger gap means the car
                    stopped, so a new trip begins. Reads are ordered by actual timestamp, <em>not</em> split at
                    midnight — so an overnight hop (11:50 PM → 12:01 AM) is kept as one route. Raising the gap
                    merges more reads into longer routes; lowering it splits them into shorter ones.
                    <br /><br />
                    This is a <em>historical</em> view — what the vehicle has done most. It differs from
                    <strong> Next-Camera Prediction</strong> below, which is forward-looking: given a camera,
                    it estimates where the vehicle goes <em>next</em> and with what probability. A route can be
                    frequent here yet still be only one of several likely next moves at each step.
                  </Typography>
                </Box>
              )}

              {/* Trip-gap selector — governs both Common Routes and Next-Camera Prediction */}
              <Box sx={{ bgcolor: '#faf9ff', border: '1px solid #ede9fe', borderRadius: '10px', p: '12px 16px 6px', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: '#475569' }}>
                    Trip gap — split trips when reads are more than
                  </Typography>
                  <Box sx={{
                    fontSize: '13px', fontWeight: 700, color: '#6d28d9',
                    bgcolor: '#f5f3ff', px: '10px', py: '3px', borderRadius: '99px', flexShrink: 0,
                  }}>
                    {fmtGap(maxGapMin)} apart
                  </Box>
                </Box>
                <Slider
                  value={maxGapMin}
                  onChange={(e, val) => setMaxGapMin(val)}
                  min={30} max={480} step={30}
                  valueLabelDisplay="auto"
                  valueLabelFormat={fmtGap}
                  marks={[
                    { value: 30, label: '30m' },
                    { value: 60, label: '1h' },
                    { value: 120, label: '2h' },
                    { value: 240, label: '4h' },
                    { value: 360, label: '6h' },
                    { value: 480, label: '8h' },
                  ]}
                  sx={{
                    color: '#6d28d9', mt: 0.5,
                    '& .MuiSlider-markLabel': { fontSize: '10px', color: '#94a3b8' },
                    '& .MuiSlider-thumb': {
                      '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 8px rgba(109,40,217,.16)' },
                    },
                  }}
                />
              </Box>

              {(() => {
                const routeList = stats.sequences3.length ? stats.sequences3 : stats.sequences;
                const legTime = {};
                for (const s of stats.segments) legTime[`${s.from}|||${s.to}`] = s.medianMin;
                return routeList.length
                  ? routeList.map((s, i) => {
                      const stops = s.seq.split(' → ');
                      return (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'stretch', gap: 1, mb: 1.75, flexWrap: 'nowrap' }}>
                          <Box sx={{
                            bgcolor: '#f5f3ff', color: '#6d28d9', fontWeight: 700,
                            fontSize: '11px', px: '8px', py: '3px', borderRadius: '99px', flexShrink: 0, alignSelf: 'center',
                          }}>×{s.count}</Box>
                          {stops.map((stop, si) => (
                            <React.Fragment key={si}>
                              {si > 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#94a3b8', px: '2px' }}>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2">
                                    <line x1="5" y1="12" x2="19" y2="12"/>
                                    <polyline points="12 5 19 12 12 19"/>
                                  </svg>
                                  <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap', mt: '1px' }}>
                                    {fmtMins(legTime[`${stops[si - 1]}|||${stop}`])}
                                  </Typography>
                                </Box>
                              )}
                              <Box sx={{
                                display: 'flex', alignItems: 'flex-start', gap: '7px',
                                bgcolor: si === 0 ? '#ede9fe' : si === stops.length - 1 ? '#fef3c7' : '#f1f5f9',
                                border: `1px solid ${si === 0 ? '#ddd6fe' : si === stops.length - 1 ? '#fde68a' : '#e2e8f0'}`,
                                px: '10px', py: '6px', borderRadius: '8px',
                                flex: 1, minWidth: 0,
                              }}>
                                <Box sx={{
                                  width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, mt: '1px',
                                  bgcolor: si === 0 ? '#6d28d9' : si === stops.length - 1 ? '#d97706' : '#94a3b8',
                                  color: '#fff', fontSize: '10px', fontWeight: 800,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>{si + 1}</Box>
                                <Typography sx={{
                                  fontSize: '13px', color: '#1e293b', fontWeight: 500,
                                  overflow: 'hidden', display: '-webkit-box',
                                  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.3,
                                }}>{stop}</Typography>
                              </Box>
                            </React.Fragment>
                          ))}
                        </Box>
                      );
                    })
                  : <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>No repeated site sequences detected.</Typography>;
              })()}
            </Paper>

            {/* Next-camera prediction — full width */}
            <Paper elevation={0} sx={{
              borderRadius: '14px',
              boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
              p: '26px 26px 22px', mb: 2.5,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <CardTitle icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                    <line x1="16" y1="3" x2="22" y2="3"/>
                  </svg>
                }>Next-Camera Prediction<GeneratedMark /></CardTitle>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowPredictHelp(v => !v)}
                  sx={{
                    border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer',
                    color: '#6d28d9', fontWeight: 700, fontSize: '12px', borderRadius: '8px',
                    px: '10px', py: '5px', fontFamily: 'inherit', flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: '5px',
                    '&:hover': { bgcolor: '#f5f3ff' },
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  {showPredictHelp ? 'Hide' : 'How this works'}
                </Box>
              </Box>

              {showPredictHelp && (
                <Box sx={{
                  bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
                  p: '14px 16px', mb: 2, mt: 0.5,
                }}>
                  <Typography sx={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.6 }}>
                    This builds a <strong>first-order transition model</strong> (a Markov chain) from this
                    plate's reads. Every time the vehicle was seen at one camera and then another, that
                    counts as one <em>observed move</em>.
                    <br /><br />
                    • <strong>Probability</strong> = (moves from camera A → camera B) ÷ (all moves that
                    started at A). So "72%" means that historically, 72% of the time the vehicle left A it
                    was next seen at B.<br />
                    • <strong>Typical time</strong> = the <em>median</em> minutes between the two reads
                    (median ignores outliers better than an average).<br />
                    • <strong>The second step</strong> repeats the same logic from the predicted camera — the
                    most likely camera <em>after that one</em>.<br />
                    • <strong>Confidence</strong> (High / Medium / Low) reflects how much data backs the
                    prediction — both the number of departures observed from the origin and how dominant the
                    top choice is. High needs ~6+ departures with a clear favorite; Low means too few
                    observations to trust.
                    <br /><br />
                    Two consecutive reads count as a move only if they fall within the <strong>Trip gap</strong>
                    set under Common Routes above (default 4 hours) — reads are ordered by real timestamp, so
                    overnight moves are kept, but a longer gap means the car stopped and starts a fresh trip.
                    Predictions only reflect reads in the uploaded file and the current time-of-day filter.
                    They show correlation in past behavior, not a guarantee — origins with few observations
                    (flagged below) are unreliable.
                  </Typography>
                </Box>
              )}

              {stats.predictions.length ? (() => {
                const p = predMap[activeFrom];
                if (!p) return null;
                const step1 = p.nexts[0];
                const p2 = step1 ? predMap[step1.camera] : null;
                const step2 = p2 ? p2.nexts[0] : null;
                const conf = predictionConfidence(p.total, step1 ? step1.probability : 0);

                const StepArrow = ({ probability, medianMin, samples }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: '9px', my: '4px' }}>
                    <Box sx={{ color: '#cbd5e1', display: 'flex' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <polyline points="19 12 12 19 5 12"/>
                      </svg>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <Box sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 800, fontSize: '12px', px: '8px', py: '2px', borderRadius: '99px' }}>
                        {Math.round(probability * 100)}% likely
                      </Box>
                      <Box sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: '12px', px: '8px', py: '2px', borderRadius: '99px' }}>
                        {fmtMins(medianMin)}{medianMin == null ? '' : ` typical`}
                      </Box>
                    </Box>
                  </Box>
                );

                const CamChip = ({ label, kind }) => (
                  <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: '8px',
                    bgcolor: kind === 'origin' ? '#ede9fe' : kind === 'step1' ? '#fef3c7' : '#f1f5f9',
                    border: `1px solid ${kind === 'origin' ? '#ddd6fe' : kind === 'step1' ? '#fde68a' : '#e2e8f0'}`,
                    px: '12px', py: '9px', borderRadius: '10px',
                  }}>
                    <Box sx={{
                      mt: '1px', fontSize: '9px', fontWeight: 800, letterSpacing: '.05em',
                      textTransform: 'uppercase', color: '#fff', borderRadius: '5px', px: '5px', py: '2px', flexShrink: 0,
                      bgcolor: kind === 'origin' ? '#6d28d9' : kind === 'step1' ? '#d97706' : '#94a3b8',
                    }}>
                      {kind === 'origin' ? 'Seen' : kind === 'step1' ? 'Next' : 'Then'}
                    </Box>
                    <Typography sx={{ fontSize: '13.5px', color: '#1e293b', fontWeight: 600, lineHeight: 1.35 }}>
                      {label}
                    </Typography>
                  </Box>
                );

                return (
                  <>
                    {/* Origin selector (searchable) */}
                    <Typography sx={{ fontSize: '13px', color: '#475569', mb: '6px', mt: 0.5 }}>
                      If the vehicle was last picked up at:
                    </Typography>
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => { setPredOpen(o => !o); setPredSearch(''); }}
                        sx={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                          p: '10px 12px', fontSize: '13.5px', fontFamily: 'inherit', textAlign: 'left',
                          color: '#1e293b', border: `1px solid ${predOpen ? '#a78bfa' : '#cbd5e1'}`, borderRadius: '10px',
                          bgcolor: '#fff', cursor: 'pointer', fontWeight: 600,
                          boxShadow: predOpen ? '0 0 0 3px rgba(167,139,250,.25)' : 'none',
                        }}
                      >
                        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {activeFrom}
                          <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                            {'  '}· {p.total} departure{p.total !== 1 ? 's' : ''}
                          </Box>
                        </Box>
                        <Box component="span" sx={{ color: '#94a3b8', display: 'flex', flexShrink: 0, transform: predOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                        </Box>
                      </Box>

                      {predOpen && (
                        <>
                          <Box onClick={() => setPredOpen(false)} sx={{ position: 'fixed', inset: 0, zIndex: 10 }} />
                          <Box sx={{
                            position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 11,
                            bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
                            boxShadow: '0 8px 28px rgba(0,0,0,.14)', overflow: 'hidden',
                          }}>
                            <Box sx={{ p: '10px', borderBottom: '1px solid #f1f5f9' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', px: '10px' }}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                <Box
                                  component="input"
                                  autoFocus
                                  type="text"
                                  placeholder="Search cameras…"
                                  value={predSearch}
                                  onChange={e => setPredSearch(e.target.value)}
                                  sx={{ flex: 1, border: 'none', outline: 'none', bgcolor: 'transparent', py: '8px', fontSize: '13.5px', fontFamily: 'inherit', color: '#1e293b' }}
                                />
                              </Box>
                            </Box>
                            <Box sx={{ maxHeight: '260px', overflowY: 'auto', py: '4px' }}>
                              {(() => {
                                const q = predSearch.trim().toLowerCase();
                                const list = q ? stats.predictions.filter(pp => pp.from.toLowerCase().includes(q)) : stats.predictions;
                                if (!list.length) return <Typography sx={{ fontSize: '13px', color: '#94a3b8', px: '14px', py: '10px' }}>No matching cameras.</Typography>;
                                return list.map(pp => {
                                  const selected = pp.from === activeFrom;
                                  return (
                                    <Box
                                      key={pp.from}
                                      onClick={() => { setPredictFrom(pp.from); setPredOpen(false); setPredSearch(''); }}
                                      sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                                        px: '14px', py: '9px', cursor: 'pointer',
                                        bgcolor: selected ? '#f5f3ff' : 'transparent',
                                        '&:hover': { bgcolor: '#f5f3ff' },
                                      }}
                                    >
                                      <Typography sx={{ fontSize: '13px', fontWeight: selected ? 700 : 500, color: selected ? '#6d28d9' : '#334155', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {pp.from}
                                      </Typography>
                                      <Typography sx={{ fontSize: '11.5px', color: '#94a3b8', flexShrink: 0, fontWeight: 600 }}>
                                        {pp.total} dep.
                                      </Typography>
                                    </Box>
                                  );
                                });
                              })()}
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>

                    {/* Prediction confidence */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1.75, flexWrap: 'wrap' }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        bgcolor: conf.bg, border: `1px solid ${conf.border}`, borderRadius: '99px', px: '10px', py: '4px',
                      }}>
                        <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', bgcolor: conf.color }} />
                        <Typography sx={{ fontSize: '12px', fontWeight: 800, color: conf.color }}>
                          {conf.level} confidence
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>
                        based on {p.total} observed departure{p.total !== 1 ? 's' : ''} from this camera
                      </Typography>
                    </Box>

                    {conf.level === 'Low' && (
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: '7px', mb: 1.75,
                        bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', p: '8px 11px',
                      }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                          <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        <Typography sx={{ fontSize: '12px', color: '#991b1b', fontWeight: 600 }}>
                          Only {p.total} departure{p.total !== 1 ? 's' : ''} observed from this camera — treat this prediction as a weak signal.
                        </Typography>
                      </Box>
                    )}

                    {/* Predicted chain */}
                    {step1 ? (
                      <Box>
                        <CamChip label={activeFrom} kind="origin" />
                        <StepArrow probability={step1.probability} medianMin={step1.medianMin} />
                        <CamChip label={step1.camera} kind="step1" />
                        {step2 && (
                          <>
                            <StepArrow probability={step2.probability} medianMin={step2.medianMin} />
                            <CamChip label={step2.camera} kind="step2" />
                          </>
                        )}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>
                        No onward moves recorded from this camera.
                      </Typography>
                    )}

                    {/* Alternatives */}
                    {p.nexts.length > 1 && (
                      <Box sx={{ mt: 2, pt: 1.75, borderTop: '1px solid #f1f5f9' }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', mb: 1 }}>
                          Other possible next cameras
                        </Typography>
                        {p.nexts.slice(1, 6).map(n => (
                          <Box key={n.camera} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: '7px' }}>
                            <Typography sx={{ fontSize: '13px', color: '#334155', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {n.camera}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                              <Box sx={{ bgcolor: '#f5f3ff', color: '#6d28d9', fontWeight: 700, fontSize: '11.5px', px: '7px', py: '2px', borderRadius: '99px' }}>
                                {Math.round(n.probability * 100)}%
                              </Box>
                              <Box sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: '11.5px', px: '7px', py: '2px', borderRadius: '99px' }}>
                                {fmtMins(n.medianMin)}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                );
              })() : (
                <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>
                  Not enough sequential reads to model camera-to-camera transitions.
                </Typography>
              )}
            </Paper>

            {/* PDF download */}
            <Button
              className="lpr-no-print"
              variant="contained" fullWidth
              onClick={downloadPDF}
              startIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9"/>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                  <rect x="6" y="14" width="12" height="8"/>
                </svg>
              }
              sx={{
                mb: 2.5, p: '14px 20px', bgcolor: '#4c1d95',
                fontSize: '15px', fontWeight: 600, borderRadius: '10px',
                '&:hover': { bgcolor: '#3b1378' },
              }}
            >
              Download PDF
            </Button>

            {/* Timeline */}
            <Paper elevation={0} sx={{
              borderRadius: '14px',
              boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
              p: '26px 26px 22px',
            }}>
              <CardTitle icon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              }>Chronological Timeline</CardTitle>

              {Object.keys(filteredTimeline).length === 0 ? (
                <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>No timeline data.</Typography>
              ) : (
                Object.keys(filteredTimeline).sort().reverse().map(dateKey => {
                  const reads = filteredTimeline[dateKey];
                  const isOpen = openDates.has(dateKey);
                  return (
                    <Box key={dateKey} sx={{ mb: 1.5 }}>
                      {/* Date header */}
                      <Box
                        onClick={() => toggleDate(dateKey)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.25,
                          p: '10px 14px', bgcolor: '#f8fafc',
                          border: '1px solid #e2e8f0', borderRadius: '8px',
                          cursor: 'pointer', userSelect: 'none',
                          '&:hover': { bgcolor: '#f1f5f9' },
                        }}
                      >
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', flex: 1 }}>
                          {fmtDateFull(dateKey)}
                        </Typography>
                        <Box sx={{
                          fontSize: '11px', bgcolor: '#ede9fe', color: '#5b21b6',
                          fontWeight: 700, px: '9px', py: '2px', borderRadius: '99px',
                        }}>
                          {reads.length} read{reads.length !== 1 ? 's' : ''}
                        </Box>
                        <svg
                          width="14" height="14" viewBox="0 0 24 24" fill="none"
                          stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          style={{ transition: 'transform .2s', transform: isOpen ? 'rotate(90deg)' : 'none', flexShrink: 0 }}>
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </Box>

                      {/* Date body */}
                      {isOpen && (
                        <Box sx={{ pt: 1.25, pb: '2px', pl: 2 }}>
                          <Box sx={{ borderLeft: '2px solid #e2e8f0', pl: 2, ml: '4px' }}>
                            {[...reads].reverse().map((r, ri) => (
                              <Box key={ri} sx={{
                                display: 'flex', gap: 1.75, py: '10px',
                                position: 'relative',
                                '&::before': {
                                  content: '""', position: 'absolute',
                                  left: '-22px', top: '16px',
                                  width: '8px', height: '8px',
                                  bgcolor: '#6d28d9', borderRadius: '50%',
                                  border: '2px solid #fff',
                                  boxShadow: '0 0 0 2px #6d28d9',
                                },
                              }}>
                                <Typography sx={{
                                  fontSize: '13px', fontWeight: 700, color: '#6d28d9',
                                  minWidth: '72px', mt: '1px', flexShrink: 0,
                                }}>{r.time}</Typography>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontSize: '13px', color: '#0f172a', fontWeight: 500 }}>
                                      {r.camera}
                                    </Typography>
                                    {r.lat != null && r.lng != null && (
                                      <Box
                                        component="button"
                                        type="button"
                                        className="lpr-no-print"
                                        title="Show camera location on map"
                                        onClick={() => setLocModal({ camera: r.camera, lat: r.lat, lng: r.lng })}
                                        sx={{
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          flexShrink: 0, width: '22px', height: '22px', p: 0, cursor: 'pointer',
                                          color: '#6d28d9', bgcolor: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '6px',
                                          '&:hover': { bgcolor: '#ede9fe', color: '#5b21b6' },
                                        }}
                                      >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                          <circle cx="12" cy="10" r="3"/>
                                        </svg>
                                      </Box>
                                    )}
                                  </Box>
                                  {r.site && r.site !== r.camera && (
                                    <Typography sx={{ fontSize: '12px', color: '#64748b', mt: '2px' }}>{r.site}</Typography>
                                  )}
                                  {r.agency && (
                                    <Typography sx={{ fontSize: '11px', color: '#64748b', mt: '2px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                      </svg>
                                      {r.agency}
                                    </Typography>
                                  )}
                                  {r.confidence > 0 && (
                                    <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: '2px' }}>
                                      Confidence: {r.confidence}%
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })
              )}
            </Paper>
          </>
        )}
      </Container>

      <CameraMapModal
        open={!!locModal}
        onClose={() => setLocModal(null)}
        camera={locModal?.camera}
        lat={locModal?.lat}
        lng={locModal?.lng}
      />
    </>
  );
}
